// What actually went into the prompt.
//
// §2 of the 2026-08-11 handoff, steps 1 and 2: describe the sources that exist
// today and report on them. This changes NO behaviour — nothing here edits a
// prompt. It only says what the prompt turned out to contain, which is the
// thing nothing in this codebase has ever been able to answer.
//
// Every check the project owns reads the code against itself: tests, eslint,
// the build, audit-codebase.py. observe-production.mjs reads production rows.
// None of them can see the assembled prompt, and that is precisely where the
// 2026-08-10 bugs were obvious and invisible in source:
//
//   19 mentions of one character's name against 4 for the whole cast
//   60 voice samples for the anchor against 5 each for everyone else
//   5,319 characters of boilerplate sitting after a 1,177-character message
//   a 50,000-character budget that kept 239,595 characters
//   "Red" returning nothing, every turn, for 148 turns
//
// Not one of those is a wrong line of code. They are properties of the output.

/** Layers, in the order the handoff names them. */
export const LAYER = {
  WORLD: 'world',        // species lore, world rules, physics — true for everyone
  CHARACTER: 'character', // canon, overrides, secrets — true for this one
  SITUATION: 'situation', // this scene, this writer, this history — true right now
};

const approxTokens = (s) => Math.ceil(String(s || '').length / 4);

/**
 * Describe an assembled prompt.
 *
 * @param {Array} sources  [{ id, layer, scope, text, required }]
 *   `text` is the section as it will actually ship — empty string means the
 *   source produced nothing, which is the case worth surfacing.
 * @returns {{ included, empty, requiredEmpty, totalChars, totalTokens, byLayer }}
 */
export function buildManifest(sources) {
  const list = (sources || []).filter(s => s && s.id);
  const rows = list.map(s => ({
    id: s.id,
    layer: s.layer || LAYER.SITUATION,
    scope: s.scope || 'unknown',
    required: !!s.required,
    chars: String(s.text || '').length,
    tokens: approxTokens(s.text),
  }));

  const included = rows.filter(r => r.chars > 0);
  const empty = rows.filter(r => r.chars === 0);
  const byLayer = {};
  for (const r of included) {
    byLayer[r.layer] = (byLayer[r.layer] || 0) + r.tokens;
  }

  return {
    included,
    empty,
    // THE INVARIANT. A required source that came back empty is the shape of
    // "werewolf.reproduction is NULL" and "recency returned 0 rows because it
    // had no channel scope" — both found weeks later, in play.
    requiredEmpty: empty.filter(r => r.required),
    totalChars: rows.reduce((n, r) => n + r.chars, 0),
    totalTokens: rows.reduce((n, r) => n + r.tokens, 0),
    byLayer,
  };
}

/**
 * Per-character share of the prompt, for ensembles.
 *
 * The cast bug was not missing data — every NPC had a full anchor. It was that
 * one character occupied most of the prompt. A ratio makes that visible in one
 * number instead of requiring someone to notice it in play.
 *
 * @param {string} prompt      the assembled text
 * @param {Array}  cast        [{ id, name }]
 * @returns {{ counts, max, min, ratio, starved }}
 */
export function castBalance(prompt, cast) {
  const text = String(prompt || '');
  const counts = {};
  for (const c of cast || []) {
    if (!c?.name) continue;
    const first = String(c.name).trim().split(/\s+/)[0];
    if (!first) continue;
    const re = new RegExp(`\\b${first.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
    counts[c.name] = (text.match(re) || []).length;
  }
  const vals = Object.values(counts);
  if (!vals.length) return { counts, max: 0, min: 0, ratio: 1, starved: [] };
  const max = Math.max(...vals);
  const min = Math.min(...vals);
  return {
    counts,
    max,
    min,
    ratio: min > 0 ? max / min : Infinity,
    // Named in the cast and barely present in the prompt that is supposed to
    // be voicing them. Red scored 0 here for 148 consecutive turns.
    starved: Object.entries(counts).filter(([, n]) => n === 0).map(([name]) => name),
  };
}

/**
 * Assemble a prompt from its declared sources, and describe it in the same
 * pass. §2 step 3.
 *
 * The ONE list is the point. Before this, the prompt was a template literal
 * interpolating ~20 variables and the manifest was a second, hand-written list
 * of the same variables — two copies of one thing, which is the pattern behind
 * every bug in docs/ROOT-CAUSES-2026-08-10.md. A source added to the prompt
 * and forgotten in the manifest would be invisible to exactly the tool built
 * to make it visible.
 *
 * Concatenation is bare, with no separator, because that is precisely what the
 * template literal did — the sections carry their own leading newlines. This
 * function is a faithful replacement, not an improvement; changing the joining
 * would change every prompt.
 *
 * @param {Array} sources same shape as buildManifest
 * @returns {{ text: string, manifest: object }}
 */
export function assemblePrompt(sources) {
  const list = (sources || []).filter(s => s && s.id);
  return {
    text: list.map(s => String(s.text ?? '')).join(''),
    manifest: buildManifest(list),
  };
}

/** One-line-per-source dump. Dev only — never call this on a hot path. */
export function formatManifest(manifest, balance = null) {
  if (!manifest) return '';
  const lines = [
    `prompt manifest — ${manifest.included.length} sources, ~${manifest.totalTokens.toLocaleString()} tokens`,
  ];
  for (const layer of [LAYER.WORLD, LAYER.CHARACTER, LAYER.SITUATION]) {
    const inLayer = manifest.included.filter(r => r.layer === layer);
    if (!inLayer.length) continue;
    lines.push(`  ${layer} (~${(manifest.byLayer[layer] || 0).toLocaleString()} tok)`);
    for (const r of inLayer.sort((a, b) => b.tokens - a.tokens)) {
      lines.push(`    ${r.id.padEnd(26)} ${String(r.tokens).padStart(6)} tok  [${r.scope}]`);
    }
  }
  if (manifest.empty.length) {
    lines.push(`  EMPTY: ${manifest.empty.map(r => r.id).join(', ')}`);
  }
  if (manifest.requiredEmpty.length) {
    lines.push(`  ⚠ REQUIRED BUT EMPTY: ${manifest.requiredEmpty.map(r => r.id).join(', ')}`);
  }
  if (balance) {
    lines.push(`  cast mentions: ${Object.entries(balance.counts).map(([n, c]) => `${n} ${c}`).join(', ')}`);
    if (balance.starved.length) lines.push(`  ⚠ NEVER MENTIONED: ${balance.starved.join(', ')}`);
    else if (balance.ratio > 3) lines.push(`  ⚠ cast imbalance ${balance.ratio.toFixed(1)}:1`);
  }
  return lines.join('\n');
}
